/**
 * Client-side cart line model and merge rules (aligned with process-order).
 * Totals and stock are NOT computed here — backend is authoritative.
 */

import type { NormalizedProduct, NormalizedVariant } from './product'

/** Must match supabase/functions/process-order/index.ts */
export const MAX_QUANTITY_PER_LINE = 999
export const MAX_DISTINCT_LINES = 50

export interface CartItem {
  product_id: string
  variant_id: string | null
  quantity: number
  /**
   * Backend-alignment flag persisted at add-to-cart time.
   * Checkout must rely on this (no network dependency) to enforce variant_id presence.
   */
  hasVariants: boolean
  /** Optional display snapshot (non-authoritative). */
  product_name?: string
}

function cartLineKey(productId: string, variantId: string | null): string {
  return variantId ? `${productId}:${variantId}` : `${productId}:`
}

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/

function isUuid(s: unknown): s is string {
  return typeof s === 'string' && UUID_RE.test(s)
}

function clampQuantity(qty: number): number {
  if (!Number.isFinite(qty)) return 1
  const n = Math.floor(qty)
  if (n < 1) return 1
  return Math.min(n, MAX_QUANTITY_PER_LINE)
}

/**
 * Merge lines by (product_id, variant_id) with SQL-compatible null semantics.
 */
export function mergeCartLines(items: CartItem[]): CartItem[] {
  const map = new Map<string, CartItem>()
  for (const line of items) {
    const pid = line.product_id
    const vid = line.variant_id ?? null
    const key = cartLineKey(pid, vid)
    const qty = clampQuantity(line.quantity)
    const prev = map.get(key)
    if (prev) {
      const nextQty = clampQuantity(prev.quantity + qty)
      map.set(key, {
        product_id: pid,
        variant_id: vid,
        quantity: nextQty,
        hasVariants: prev.hasVariants || line.hasVariants,
        product_name: prev.product_name || line.product_name,
      })
    } else {
      map.set(key, {
        product_id: pid,
        variant_id: vid,
        quantity: qty,
        hasVariants: line.hasVariants === true,
        product_name: line.product_name,
      })
    }
  }
  return Array.from(map.values()).slice(0, MAX_DISTINCT_LINES)
}

export type AddToCartErrorCode =
  | 'variant_required'
  | 'quantity_overflow'
  | 'cart_capacity'
  | 'unresolved_variant'

export type AddToCartResult =
  | { ok: true; cart: CartItem[] }
  | { ok: false; code: AddToCartErrorCode; message: string }

/**
 * Add or merge a line. Pass resolved variantId (uuid) for variable products; null for simple.
 */
export function addToCart(
  current: CartItem[],
  product: NormalizedProduct,
  _variant: NormalizedVariant | null,
  variantId: string | null,
  qty: number
): AddToCartResult {
  if (product.hasVariants && !variantId) {
    return {
      ok: false,
      code: 'variant_required',
      message: 'Les produits à variantes nécessitent un variant_id avant d’ajouter au panier.',
    }
  }
  if (!product.hasVariants && variantId) {
    /* Backend accepts null variant for simple; strip for consistency */
    variantId = null
  }

  const quantity = clampQuantity(qty)
  const key = cartLineKey(product.id, variantId)
  const map = new Map<string, CartItem>()
  for (const line of mergeCartLines(current)) {
    map.set(cartLineKey(line.product_id, line.variant_id), line)
  }

  const prev = map.get(key)
  const nextQty = clampQuantity((prev?.quantity ?? 0) + quantity)
  if (nextQty > MAX_QUANTITY_PER_LINE) {
    return {
      ok: false,
      code: 'quantity_overflow',
      message: `Maximum ${MAX_QUANTITY_PER_LINE} unités par ligne.`,
    }
  }

  if (!prev && map.size >= MAX_DISTINCT_LINES) {
    return {
      ok: false,
      code: 'cart_capacity',
      message: `Maximum ${MAX_DISTINCT_LINES} lignes distinctes par commande.`,
    }
  }

  map.set(key, {
    product_id: product.id,
    variant_id: variantId,
    quantity: nextQty,
    hasVariants: product.hasVariants === true,
    product_name: product.name,
  })

  const cart = mergeCartLines(Array.from(map.values()))
  if (cart.length > MAX_DISTINCT_LINES) {
    return {
      ok: false,
      code: 'cart_capacity',
      message: `Maximum ${MAX_DISTINCT_LINES} lignes distinctes par commande.`,
    }
  }

  return { ok: true, cart }
}

/**
 * Sanitize cart loaded from persistence (localStorage).
 * Strict gatekeeper rules:
 * - remove invalid UUID product_id / variant_id
 * - clamp quantity 1..999
 * - enforce max 50 distinct lines
 * - if hasVariants=true, variant_id must exist
 */
export function sanitizeCartItems(raw: unknown): CartItem[] {
  if (!Array.isArray(raw)) return []
  const out: CartItem[] = []
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const it = r as Record<string, unknown>
    const product_id = it.product_id
    const variant_id = it.variant_id ?? null
    const quantity = clampQuantity(Number(it.quantity))
    const hasVariants = it.hasVariants === true
    const product_name =
      typeof it.product_name === 'string' && it.product_name.trim()
        ? it.product_name.trim().slice(0, 200)
        : undefined

    if (!isUuid(product_id)) continue
    if (variant_id != null && !isUuid(variant_id)) continue
    if (hasVariants && !variant_id) continue

    out.push({
      product_id,
      variant_id: (variant_id as string | null) ?? null,
      quantity,
      hasVariants,
      ...(product_name ? { product_name } : {}),
    })
    if (out.length >= MAX_DISTINCT_LINES * 2) break
  }
  return mergeCartLines(out).slice(0, MAX_DISTINCT_LINES)
}

export function removeCartLine(
  current: CartItem[],
  productId: string,
  variantId: string | null
): CartItem[] {
  const key = cartLineKey(productId, variantId)
  return mergeCartLines(current).filter(
    (l) => cartLineKey(l.product_id, l.variant_id) !== key
  )
}

export function setLineQuantity(
  current: CartItem[],
  productId: string,
  variantId: string | null,
  qty: number
): CartItem[] {
  if (qty <= 0) {
    return removeCartLine(current, productId, variantId)
  }
  const key = cartLineKey(productId, variantId)
  return mergeCartLines(
    mergeCartLines(current).map((l) =>
      cartLineKey(l.product_id, l.variant_id) === key
        ? { ...l, quantity: clampQuantity(qty) }
        : l
    )
  )
}
